import { TourBuilderStoreEvents } from '../domain/events/TourBuilderStoreEvents';
import { TourBuilderStoreIoC } from '../domain/tourBuilderIoC';
import { Scene, SceneGuid } from '../domain/types/Scene';
import { SceneLink, SceneLinkGuid } from '../domain/types/SceneLink';
import { changeLinkCoordinates, ChangeLinkCoordinatesUseCase } from '../domain/useCases/links/changeLinkCoordinates';
import {
  ChangeLinkDestinationUseCase,
  changeLinkDestinationUseCaseHandler,
} from '../domain/useCases/links/changeLinkDestination';
import { changeLinkIconHandler, ChangeLinkIconUseCase } from '../domain/useCases/links/changeLinkIcon';
import { DeleteLinkUseCase, deleteLinkUseCaseHandler } from '../domain/useCases/links/deleteLink';
import { LinkTwoScenesUseCase, linkTwoScenesUseCaseHandler } from '../domain/useCases/links/linkTwoScenes';
import { AddScenesUseCase, addScenesUseCaseHandler } from '../domain/useCases/scenes/addScene';
import { deleteSceneUseCase, DeleteSceneUseCase } from '../domain/useCases/scenes/deleteScene';
import { replaceSceneUseCase, ReplaceSceneUseCase } from '../domain/useCases/scenes/replaceScene';
import { synchronizeSceneHandler, SynchronizeSceneUseCase } from '../domain/useCases/scenes/synchronizeScene';
import {
  ChangeLinkDestinationLabelUseCase,
  changeLinkDestinationLabelUseCaseHandler,
} from './useCases/links/changeLinkDestinationLabel';
import { ChangeLinkTiltUseCase, changeLinkTiltUseCaseHandler } from './useCases/links/changeLinkTilt';
import { TourDto, TourSceneDto } from '@ael/core-api';
import { createStore, StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DevtoolsOptions } from 'zustand/middleware/devtools';

export type TourBuilderStore = {
  scenes: Map<SceneGuid, Scene>;
  scenesById: SceneGuid[];
  links: Map<SceneLinkGuid, SceneLink>;
  subscriptions: TourBuilderStoreEvents;
  actions: {
    addScenes: (params: AddScenesUseCase) => Promise<void>;
    replaceScene: (useCase: ReplaceSceneUseCase) => Promise<void>;
    synchronizeScene: (useCase: SynchronizeSceneUseCase) => Promise<void>;
    deleteScene: (useCase: DeleteSceneUseCase) => void;

    linkTwoScenes: (useCase: LinkTwoScenesUseCase) => void;
    changeLinkDestination: (useCase: ChangeLinkDestinationUseCase) => void;
    changeLinkDestinationLabel: (useCase: ChangeLinkDestinationLabelUseCase) => void;
    changeLinkIcon: (useCase: ChangeLinkIconUseCase) => void;
    changeLinkCoordinates: (useCase: ChangeLinkCoordinatesUseCase) => void;
    deleteLink: (useCase: DeleteLinkUseCase) => void;
    changeLinkTilt: (useCase: ChangeLinkTiltUseCase) => void;
  };
};

export const createTourBuilderStore = (
  ioc: TourBuilderStoreIoC,
  initState?: Partial<TourBuilderStore>
): StoreApi<TourBuilderStore> => {
  const currentDateTime = new Date().toUTCString();
  const reduxDevToolsName = `TourBuilderStore ${currentDateTime}`;
  const devtoolOptions: DevtoolsOptions = {
    name: reduxDevToolsName,
    serialize: true,
    enabled: process.env.NODE_ENV === 'development',
  };

  const eventHandlers = new TourBuilderStoreEvents();

  const zustandStore = createStore<TourBuilderStore>()(
    devtools((set, getState) => {
      // Wraps around action handling to inject necessary dependencies and logs into redux devtools
      const wrap = async <TUseCase>(name: string, useCase: TUseCase, handler: TUseCaseHandler<TUseCase>) => {
        const returnedValue = await handler(useCase, getState, ioc, eventHandlers);
        return set(returnedValue, false, { type: name, payload: useCase });
      };
      const wrapSync = <TUseCase>(name: string, useCase: TUseCase, handler: TUseCaseHandlerSync<TUseCase>) => {
        const returnedValue = handler(useCase, getState, ioc, eventHandlers);
        set(returnedValue, false, { type: name, payload: useCase });
      };

      const store: TourBuilderStore = {
        scenes: initState?.scenes ?? new Map<SceneGuid, Scene>(),
        scenesById: initState?.scenesById ?? [],
        links: initState?.links ?? new Map<SceneLinkGuid, SceneLink>(),
        actions: {
          addScenes: async (useCase: AddScenesUseCase) => await wrap('SCENE_ADDED', useCase, addScenesUseCaseHandler),
          replaceScene: async (useCase: ReplaceSceneUseCase) =>
            await wrap('SCENE_REPLACED', useCase, replaceSceneUseCase),
          synchronizeScene: async (useCase: SynchronizeSceneUseCase) =>
            await wrap('SCENE_SYNCHRONIZED', useCase, synchronizeSceneHandler),
          deleteScene: (useCase: DeleteSceneUseCase) => wrapSync('SCENE_DELETED', useCase, deleteSceneUseCase),
          linkTwoScenes: (useCase: LinkTwoScenesUseCase) =>
            wrapSync('LINK_ADDED', useCase, linkTwoScenesUseCaseHandler),
          changeLinkDestination: (useCase: ChangeLinkDestinationUseCase) =>
            wrapSync('LINK_DESTINATION_CHANGED', useCase, changeLinkDestinationUseCaseHandler),
          changeLinkDestinationLabel: (useCase: ChangeLinkDestinationLabelUseCase) =>
            wrapSync('LINK_DESTINATION_LABEL_CHANGED', useCase, changeLinkDestinationLabelUseCaseHandler),
          changeLinkCoordinates: (useCase: ChangeLinkCoordinatesUseCase) =>
            wrapSync('LINK_COORDINATES_CHANGED', useCase, changeLinkCoordinates),
          changeLinkIcon: (useCase: ChangeLinkIconUseCase) =>
            wrapSync('LINK_ICON_CHANGED', useCase, changeLinkIconHandler),
          deleteLink: (useCase: DeleteLinkUseCase) => wrapSync('LINK_DELETED', useCase, deleteLinkUseCaseHandler),
          changeLinkTilt: (useCase: ChangeLinkTiltUseCase) =>
            wrapSync('LINK_TILT_CHANGED', useCase, changeLinkTiltUseCaseHandler),
        },
        subscriptions: eventHandlers,
      };
      return store;
    }, devtoolOptions)
  );

  return zustandStore;
};

export function createVirtualTourConfigurationFromStore(store: TourBuilderStore): TourDto {
  const virtualTourConfiguration: TourDto = {
    tourScenes: [],
  };

  store.scenesById.forEach((sceneId) => {
    const scene = store.scenes.get(sceneId);
    if (!scene) {
      throw Error('Discrepancy between sceneIds and scenes in the state.');
    }

    const tourSceneDto: TourSceneDto = {
      guid: scene.guid,
      tourScenePanorama: scene.renderSettings,
      links: [],
    };

    virtualTourConfiguration.tourScenes.push(tourSceneDto);
  });

  return virtualTourConfiguration;
}

type TUseCaseHandler<TUseCase> = (
  useCase: TUseCase,
  getState: () => TourBuilderStore,
  ioc: TourBuilderStoreIoC,
  eventHandlers: TourBuilderStoreEvents
) => Promise<Partial<TourBuilderStore>>;

type TUseCaseHandlerSync<TUseCase> = (
  useCase: TUseCase,
  getState: () => TourBuilderStore,
  ioc: TourBuilderStoreIoC,
  eventHandlers: TourBuilderStoreEvents
) => Awaited<ReturnType<TUseCaseHandler<TUseCase>>>;
